// Componente Modal para rolagem de dados
import React, { useState, useCallback, useEffect } from 'react';
import type {
  AtributoTestavel, DiceGroup, DiceModalProps, DiceRule, DiceType, DiceRollResult, SavedRoll,
} from '@/types/dice';
import { apiService } from '@/services/api';
import { DiceTray } from './components/DiceTray';
import type { EspelhoDaBandeja } from './components/DiceTray';
import type { JogadaExibida } from './components/ResultadoDaJogada';
import type { SharedDiceModalState, SharedDiceRollingState } from './GameControls/useDiceControls';

// Dado do teste avulso do jogador. O mestre pode variar o dado ao PEDIR um
// teste (fase 3 de docs/PLANO_TESTE_ATRIBUTO.md); aqui e sempre D20.
const DADO_DO_TESTE: DiceType = 'D20';

export const DiceModal: React.FC<DiceModalProps & {
  onRollDice?: (
    diceType: DiceType,
    characterId?: string,
    sessionId?: string,
    attributeId?: string,
    /** Bandeja (fase 3): varios dados numa jogada so. */
    dice?: DiceGroup[],
    /** Chips de atributo (fase 4): parcelas da ficha somadas. */
    attributeIds?: string[]
  ) => Promise<DiceRollResult | null>;
  isShared?: boolean;
  canInteract?: boolean;
  rollerName?: string;
  sessionId?: string;
  /** Teste pedido pelo mestre nesta sessao compartilhada (fase 3). Quando
   *  presente, o modal deixa de oferecer escolha: o teste ja esta definido. */
  sharedTest?: {
    attributeId: string;
    attributeName: string;
    difficulty?: number;
    bonus?: number;
    /** Vantagem / desvantagem (fase 2 da rolagem classica): a mesa ve antes
     *  do dado, pelo mesmo motivo da dificuldade. */
    advantage?: 'vantagem' | 'desvantagem';
    /** O que esta sendo tentado, nomeado pelo mestre. A mesa le ANTES do
     *  dado: e o que transforma a espera em cena. */
    actionName?: string;
    requestedByName?: string;
  };
  /** Ficha usada no modo "Testar atributo". Separada do characterId da rolagem
   *  crua de proposito: o teste EXIGE ficha (o servidor le o atributo nela),
   *  enquanto a rolagem solta continua funcionando sem personagem nenhum. */
  testCharacterId?: string;
  testCharacterAttributes?: Record<string, number>;
  /** Espelho da montagem (fase A): o que quem monta ja escolheu. */
  montagem?: SharedDiceModalState['montagem'];
  /** A rolagem DESTA sessao acontecendo/congelada (filtrada por quem chama). */
  aoVivo?: SharedDiceRollingState;
  /** Quem ve e o mestre da mesa: valida a jogada ou pede para repetir. */
  souMestre?: boolean;
  /** Ha mestre com socket vivo na mesa? Sem ele o servidor recusa a rolagem
   *  (fase B, decisao 1): a UI explica antes de o jogador tentar. */
  mestreOnline?: boolean;
  /** Id da jogada que o mestre mandou repetir (chega pelo WebSocket). */
  repetida?: string | null;
}> = ({
  isOpen,
  onClose,
  tableId,
  characterId,
  onRollComplete,
  onRollDice,
  isShared = false,
  canInteract = true,
  rollerName,
  sessionId,
  sharedTest,
  testCharacterId,
  testCharacterAttributes,
  montagem,
  aoVivo,
  souMestre = false,
  mestreOnline = true,
  repetida = null
}) => {
  const [isRolling, setIsRolling] = useState(false);
  const [rollingDice, setRollingDice] = useState<DiceType | null>(null);
  const [atributos, setAtributos] = useState<AtributoTestavel[]>([]);
  // Regra de resolucao da mesa (fase 3): vem do game_config, o jogador nao
  // escolhe. Ausente = somar tudo, o comportamento de sempre.
  const [regra, setRegra] = useState<DiceRule | null>(null);
  // Rolagens salvas da campanha (fase 4): catalogo do game_config.
  const [salvas, setSalvas] = useState<SavedRoll[]>([]);
  // Jogada que rolou e nao chegou ao chat (o jogador fechou a tela no meio).
  // O servidor lembra; a bandeja a devolve com o botao de enviar.
  const [pendente, setPendente] = useState<DiceRollResult | null>(null);

  useEffect(() => {
    if (!isOpen || !tableId) return;
    let cancelado = false;
    apiService.request<{ pendente: DiceRollResult | null }>(
      `/dice/tables/${tableId}/pendente`, { method: 'GET' }
    )
      .then(resposta => { if (!cancelado) setPendente(resposta?.pendente ?? null); })
      .catch(() => { if (!cancelado) setPendente(null); });
    return () => { cancelado = true; };
  }, [isOpen, tableId]);
  // Nomes dos atributos vem do game_config da mesa; os valores, da ficha.
  // So carrega com o modal aberto e havendo personagem — quem nao tem ficha
  // continua vendo apenas os dados.
  useEffect(() => {
    if (!isOpen || !tableId || !testCharacterId || !testCharacterAttributes) {
      setAtributos([]);
      return;
    }
    let cancelado = false;
    apiService.getGameConfig(tableId)
      .then((config: { attributes?: { id: string; name: string }[] }) => {
        if (cancelado) return;
        const testaveis = (config?.attributes ?? [])
          .filter((attr) => testCharacterAttributes[attr.id] !== undefined)
          .map((attr) => ({ id: attr.id, name: attr.name, value: testCharacterAttributes[attr.id] }));
        setAtributos(testaveis);
      })
      .catch(() => {
        // sem config, o modal segue funcionando como rolagem crua
        if (!cancelado) setAtributos([]);
      });
    return () => { cancelado = true; };
  }, [isOpen, tableId, testCharacterId, testCharacterAttributes]);

  // Regra da mesa e rolagens salvas: interessam a todo mundo, inclusive a quem
  // nao tem ficha — por isso um efeito proprio.
  useEffect(() => {
    if (!isOpen || !tableId) return;
    let cancelado = false;
    apiService.getGameConfig(tableId)
      .then((config: { dice_rule?: DiceRule; saved_rolls?: SavedRoll[] }) => {
        if (cancelado) return;
        setRegra(config?.dice_rule ?? null);
        setSalvas(config?.saved_rolls ?? []);
      })
      .catch(() => {
        // sem config, a bandeja soma tudo (o servidor decide igual)
        if (cancelado) return;
        setRegra(null);
        setSalvas([]);
      });
    return () => { cancelado = true; };
  }, [isOpen, tableId]);

  // Tecla ESC para fechar o modal (só funciona quando não está rolando).
  //
  // Na bandeja COMPARTILHADA o ESC é do mestre (decisão 12, 2026-07-27): ela
  // aparece para a mesa inteira, e se o aluno a fechasse cancelaria na frente
  // da turma o pedido que o mestre fez. O servidor recusa de qualquer jeito;
  // deixar a tecla ligada só renderia um aviso de erro a cada ESC.
  const podeRetirar = !isShared || souMestre;
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isRolling && podeRetirar) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, isRolling, onClose, podeRetirar]);

  const handleDiceClick = useCallback(async (diceType: DiceType, attributeId?: string) => {
    // Feedback tatil no proprio toque (contexto de gesto do usuario — o
    // navegador nunca bloqueia aqui; Android vibra, iOS ignora em silencio)
    if ('vibrate' in navigator) navigator.vibrate(35);

    setIsRolling(true);
    setRollingDice(diceType);

    // O modal NAO fecha mais aqui (fase A): a tela unica fica aberta ate a
    // jogada virar registro no chat. Quem rolou ve o giro e o resultado no
    // mesmo lugar — e o botao de enviar continua ao alcance.

    try {
      // Se tem função de rolagem, usar ela. Senão usar mock
      if (onRollDice) {
        // A função onRollDice já vai fazer o broadcast via backend.
        // Em modal compartilhado, envia o sessionId para o servidor validar
        // que só quem iniciou a sessão pode rolar.
        const result = await onRollDice(
          diceType,
          attributeId ? testCharacterId : characterId,
          isShared ? sessionId : undefined,
          attributeId
        );
        if (result) {
          onRollComplete?.(result);
        }
      } else {
        // Mock para desenvolvimento (não deve ser usado em produção)
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const mockResult: DiceRollResult = {
          id: `mock-${Date.now()}`,
          dice_type: diceType,
          result: Math.floor(Math.random() * parseInt(diceType.substring(1))) + 1,
          user_id: 'current-user',
          user_name: 'Você',
          table_id: tableId,
          character_id: characterId,
          character_name: undefined,
          created_at: new Date().toISOString()
        };
        
        onRollComplete?.(mockResult);
      }
      
    } catch (error) {
      console.error('Erro ao rolar dado:', error);
    } finally {
      setIsRolling(false);
      setRollingDice(null);
    }
  }, [tableId, characterId, testCharacterId, onRollComplete, onRollDice, onClose, isShared, sessionId]);

  // Bandeja (fase 3): monta a jogada e rola de uma vez. Bandeja de UM dado
  // volta ao caminho de sempre (`dice_type`), para rolar um d20 continuar
  // sendo exatamente o que sempre foi — inclusive no registro e no chat.
  const handleEnviar = useCallback(async (rollId: string) => {
    try {
      await apiService.request(`/dice/${rollId}/publicar`, { method: 'POST' });
      onClose();
      return true;
    } catch (erro) {
      console.error('Erro ao enviar a rolagem ao chat:', erro);
      return false;
    }
  }, [onClose]);

  // O mestre pede para repetir (fase B). O modal NAO fecha: a bandeja de quem
  // rolou volta a montagem — com os dados que estavam nela — para o jogador
  // corrigir o que foi apontado. Fechar tela em cima de quem joga foi
  // exatamente o erro que a fase A desfez.
  const handleRepetir = useCallback(async (rollId: string) => {
    try {
      await apiService.request(`/dice/${rollId}/repetir`, { method: 'POST' });
      return true;
    } catch (erro) {
      console.error('Erro ao pedir a repeticao da jogada:', erro);
      return false;
    }
  }, []);

  /**
   * Rola o teste PEDIDO pelo mestre, a partir da bandeja.
   *
   * Vai pelo MESMO caminho do antigo botao "Rolar D20": um `dice_type` com
   * `attribute_id`, e o servidor resolve o resto lendo a sessao. Os dois cubos
   * que a vantagem mostra sao desenho — quem rola dois dados e o servidor.
   * Mandar a bandeja como pool aqui trocaria o teste de atributo por outra
   * coisa, sem veredito e sem critico.
   */
  const handleRolarPedido = useCallback(async (): Promise<DiceRollResult | null> => {
    if (!sharedTest || !onRollDice) return null;
    if ('vibrate' in navigator) navigator.vibrate(35);
    try {
      const resultado = await onRollDice(
        DADO_DO_TESTE,
        testCharacterId,
        isShared ? sessionId : undefined,
        sharedTest.attributeId
      );
      if (resultado) onRollComplete?.(resultado);
      return resultado ?? null;
    } catch (erro) {
      console.error('Erro ao rolar o teste pedido:', erro);
      return null;
    }
  }, [sharedTest, onRollDice, testCharacterId, isShared, sessionId, onRollComplete]);

  // Espelha a montagem para a mesa (fase A): cada toque de quem monta vira um
  // POST leve; o servidor retransmite para todos. So exibicao — errar aqui
  // nunca muda a jogada, que o /roll resolve sozinho no servidor.
  const handleMudancaDaBandeja = useCallback((dados: DiceType[], refs: string[]) => {
    if (!isShared || !canInteract || !sessionId) return;
    const modifiers = refs
      .map(id => atributos.find(a => a.id === id))
      .filter((a): a is AtributoTestavel => !!a)
      .map(a => ({ id: a.id, name: a.name, value: a.value }));
    apiService.request('/dice/modal/tray', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, dice: dados, modifiers }),
    }).catch(() => {
      // espelho e cosmetico: falhar em silencio e melhor que interromper quem monta
    });
  }, [isShared, canInteract, sessionId, atributos]);

  const handleRolarBandeja = useCallback(async (
    grupos: DiceGroup[],
    atributosEscolhidos: string[] = []
  ) => {
    if (!onRollDice) return null;
    const total = grupos.reduce((soma, g) => soma + g.qty, 0);
    const umDadoSo = total === 1 ? grupos[0].type : null;

    // Um D20 com UM atributo continua sendo o TESTE DE ATRIBUTO de sempre
    // (docs/PLANO_TESTE_ATRIBUTO.md): vai por `attribute_id`, o servidor
    // resolve o veredito e o chat diz "testou Corpo". Mais de um atributo, ou
    // mais de um dado, e outra coisa — parcela de uma jogada montada.
    const eTesteDeAtributo =
      umDadoSo === DADO_DO_TESTE && atributosEscolhidos.length === 1;

    if ('vibrate' in navigator) navigator.vibrate(35);
    try {
      const resultado = await onRollDice(
        umDadoSo ?? 'D20',
        // Chips de atributo exigem ficha: o servidor le o valor nela.
        atributosEscolhidos.length ? testCharacterId : characterId,
        isShared ? sessionId : undefined,
        eTesteDeAtributo ? atributosEscolhidos[0] : undefined,
        umDadoSo ? undefined : grupos,
        !eTesteDeAtributo && atributosEscolhidos.length ? atributosEscolhidos : undefined
      );
      if (resultado) {
        onRollComplete?.(resultado);
        // O modal NAO fecha: a mesma tela em que os dados foram escolhidos e a
        // que os rola e mostra o resultado. Quem assiste ve a cerimonia; quem
        // rolou fica aqui (decisao do Paulo, 2026-07-25).
      }
      return resultado;
    } catch (error) {
      console.error('Erro ao rolar a bandeja:', error);
      return null;
    }
  }, [onRollDice, characterId, testCharacterId, isShared, sessionId, onRollComplete, onClose]);

  // A jogada DESTA sessao, como a mesa ve (fase A). O `aoVivo` chega filtrado
  // pelo chamador: e a rolagem de quem esta com a bandeja aberta, nunca uma
  // rolagem qualquer da mesa.
  const rolandoAoVivo = !!aoVivo?.isActive && aoVivo.result === undefined;
  // Vantagem: os DOIS dados vao para o spec e o `kept` diz qual valeu. Reusa o
  // vocabulario que a bandeja ja tem para descarte (dado apagado, riscado) em
  // vez de inventar um desenho novo — quem ja viu "manteve os 2 maiores" le
  // esta tela sem reaprender nada.
  const comVantagem =
    !!aoVivo?.advantage &&
    typeof aoVivo.droppedResult === 'number' &&
    typeof aoVivo.result === 'number' &&
    !aoVivo.diceSpec;
  const jogadaAoVivo: JogadaExibida | null = aoVivo && aoVivo.result !== undefined ? {
    spec: aoVivo.diceSpec ?? (comVantagem ? [
      { type: aoVivo.diceType, result: aoVivo.result as number },
      { type: aoVivo.diceType, result: aoVivo.droppedResult as number },
    ] : [{
      type: aoVivo.diceType,
      result: typeof aoVivo.result === 'number' ? aoVivo.result : 0,
    }]),
    advantage: aoVivo.advantage,
    kept: comVantagem ? [0] : aoVivo.kept,
    rule: aoVivo.rule,
    expression: aoVivo.expression,
    modifiers: aoVivo.modifiers,
    attributeName: aoVivo.attributeName,
    attributeValue: aoVivo.attributeValue,
    bonus: aoVivo.bonus,
    result: typeof aoVivo.result === 'number' ? aoVivo.result : undefined,
    total: aoVivo.total,
    difficulty: aoVivo.difficulty,
    outcome: aoVivo.outcome,
    critical: aoVivo.critical,
    actionName: aoVivo.actionName,
  } : null;

  const nomeDeQuemJoga = rollerName || 'o jogador';
  // Quem assiste ve a bandeja espelhada. O convocado de um pedido do mestre
  // tambem cai no espelho DEPOIS de rolar: a tela dele e a do pedido, sem
  // bandeja propria — e ela nao pode sumir antes de a jogada virar registro.
  const assistindo = isShared && !canInteract;
  const convocadoJaRolou =
    isShared && canInteract && !!sharedTest && (rolandoAoVivo || !!jogadaAoVivo);
  const espelho: EspelhoDaBandeja | null = (assistindo || convocadoJaRolou) ? {
    nome: nomeDeQuemJoga,
    // Com vantagem sao dois dados na bandeja antes mesmo de rolar: quem assiste
    // ve o que vai acontecer, nao so o resultado.
    dice: sharedTest
      ? ((sharedTest.advantage ? ['D20', 'D20'] : ['D20']) as DiceType[])
      : (montagem?.dice ?? []),
    modifiers: montagem?.modifiers ?? [],
    rolando: rolandoAoVivo,
    jogada: jogadaAoVivo,
    rollId: aoVivo?.rollId,
    // Quem age na jogada congelada e o MESTRE, e so ele (fase B). O convocado
    // de um pedido do mestre tambem perdeu o botao: ele nao envia a propria
    // jogada — espera a validacao, como qualquer jogador.
    possoEnviar: souMestre,
  } : null;

  if (!isOpen) return null;

  return (
    <div className="dice-modal-overlay">
      <div className={`dice-modal ${isShared ? 'shared' : ''}`}>
        <div className="dice-modal-header">
          {sharedTest ? (
            <h3>🎲 Teste de {sharedTest.attributeName}</h3>
          ) : isShared ? (
            canInteract ? (
              <h3>🎲 Escolha um dado para rolar</h3>
            ) : (
              <h3>🎲 {rollerName} está rolando dados</h3>
            )
          ) : (
            <h3>🎲 Escolha um dado para rolar</h3>
          )}
        </div>
        
        <div className="dice-modal-body">
          {sharedTest && !rolandoAoVivo && !jogadaAoVivo ? (
            <div className="dice-pedido">
              {sharedTest.actionName && (
                <p className="dice-pedido-acao" data-testid="pedido-acao">
                  {sharedTest.actionName}
                </p>
              )}
              <p className="dice-pedido-frase">
                {sharedTest.requestedByName ? `${sharedTest.requestedByName} pede` : 'O mestre pede'}
                {' '}um teste de <strong>{sharedTest.attributeName}</strong>
                {typeof sharedTest.difficulty === 'number' && (
                  <> — dificuldade <strong>{sharedTest.difficulty}</strong></>
                )}
                {typeof sharedTest.bonus === 'number' && sharedTest.bonus !== 0 && (
                  <> (bônus {sharedTest.bonus > 0 ? '+' : '−'}{Math.abs(sharedTest.bonus)})</>
                )}
                {sharedTest.advantage && (
                  <>, com <strong>{sharedTest.advantage}</strong></>
                )}
              </p>
              {/* O que a vantagem faz, em uma linha. A mecânica só é legível
                  para quem nunca jogou RPG se a tela disser o que ela é. */}
              {sharedTest.advantage && (
                <p className="dice-pedido-espera" data-testid="pedido-vantagem">
                  {sharedTest.advantage === 'vantagem'
                    ? 'Rola dois dados e vale o maior.'
                    : 'Rola dois dados e vale o menor.'}
                </p>
              )}
              {canInteract ? (
                // A BANDEJA, nao um botao (decisao do Paulo, 2026-07-31). O
                // desafio e do mestre — os dados vem montados e nao se editam —
                // mas quem rola ve os cubos e poe a mao neles. Antes daqui, o
                // convocado recebia "Rolar D20" pelado enquanto a mesa inteira
                // ja via os dados no espelho: o unico sem cubo era justamente
                // quem ia rolar.
                <DiceTray
                  regra={regra}
                  atributos={atributos}
                  disabled={isRolling}
                  onRolar={handleRolarPedido}
                  onMudanca={handleMudancaDaBandeja}
                  souMestre={souMestre}
                  mestreOnline={mestreOnline}
                  pedido={{
                    dice: (sharedTest.advantage
                      ? [DADO_DO_TESTE, DADO_DO_TESTE]
                      : [DADO_DO_TESTE]) as DiceType[],
                    atributoId: sharedTest.attributeId,
                  }}
                />
              ) : (
                <p className="dice-pedido-espera">
                  Aguardando {rollerName} rolar...
                </p>
              )}
              {/* A saida do mestre. Sem ela, um pedido que o jogador nao pode
                  responder (caiu a conexao dele, por exemplo) deixa a tela
                  congelada para a mesa inteira — e a unica saida era a tecla
                  ESC, que no celular nao existe (relato do Paulo, 2026-07-26).
                  Cancelar fecha a tela de TODOS, pelo mesmo caminho do
                  /dice/modal/close: a sessao morre no servidor e ninguem fica
                  com um pedido fantasma na tela. */}
              {!canInteract && souMestre && (
                <button
                  type="button"
                  className="dice-pedido-cancelar"
                  onClick={onClose}
                  data-testid="pedido-cancelar"
                >
                  Cancelar pedido
                </button>
              )}
            </div>
          ) : (
          // A MESMA bandeja para a mesa inteira (fase A): quem monta interage,
          // quem assiste recebe o espelho — montagem, giro e resultado, tudo
          // nesta tela, sem cerimonia por cima.
          <DiceTray
            regra={regra}
            atributos={atributos}
            salvas={salvas}
            disabled={isRolling}
            onRolar={handleRolarBandeja}
            onEnviar={handleEnviar}
            onRepetir={handleRepetir}
            pendente={espelho ? null : pendente}
            onMudanca={handleMudancaDaBandeja}
            espelho={espelho}
            souMestre={souMestre}
            mestreOnline={mestreOnline}
            repetida={repetida}
          />
          )}
        </div>
      </div>
    </div>
  );
};

export default DiceModal;